/**
 * When the page itself may raise a desktop notification (a tab that is open but hidden). Everything else is the service worker's job.
 * Rules, each one a bug that really happened (the same two unread notifications came back on the lock screen again and again):
 *  - only notifications that arrived AFTER the page first loaded and are fresh (never the unread backlog);
 *  - never when this device has a push subscription: the service worker already shows it, so the page would duplicate it;
 *  - each notification once per page lifetime, even when the bell component is remounted.
 */
export interface LocalNotifyInput {
  id: string;
  read: boolean;
  createdAt: number;
}

const FRESH_MS = 2 * 60_000;
const seen = new Set<string>();
let primed = false;

export function resetLocalNotify() {
  seen.clear();
  primed = false;
}

/** Feed every fresh list from the server; returns the notifications to show now (usually none). */
export function takeLocalNotifications(
  list: LocalNotifyInput[],
  opts: { hidden: boolean; granted: boolean; hasPush: boolean; now?: number },
): LocalNotifyInput[] {
  const now = opts.now ?? Date.now();
  if (!primed) {
    // The first answer is the backlog: remember it, show nothing.
    for (const n of list) seen.add(n.id);
    primed = true;
    return [];
  }
  const out: LocalNotifyInput[] = [];
  for (const n of list) {
    if (seen.has(n.id)) continue;
    seen.add(n.id);
    if (n.read || now - n.createdAt > FRESH_MS) continue;
    if (opts.hidden && opts.granted && !opts.hasPush) out.push(n);
  }
  return out;
}
